import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from 'locales/fr';
import { defaultCssVariables } from 'core/styled';
import { TextFontWeights } from 'components/Text';
import { computePrice } from 'utils/price';

const TitleStyle = styled.h2<{ weight: TextFontWeights; type?: TitleFontSize }>`
    font-size: ${({ theme, type }) =>
        type ? theme.base.font.size[type] : theme.feature.title.fontSize};
    font-family: ${({ theme }) => theme.feature.title.fontFamily};
    font-weight: ${({ theme, weight }) =>
        weight ? theme.base.font.weight[weight] : theme.feature.title.weight};
    color: ${({ theme }) => theme.feature.title.color};
`;

export type TitleFontSize = keyof typeof defaultCssVariables.font.size;

type TitleProps = {
    weight?: TextFontWeights;
    values?: Record<string, string | number | Date>;
    type?: TitleFontSize;
};

type Props = TitleProps &
    (
        | {
              noTranslation?: false;
              isCurrency?: false;
              currency?: string;
              text: TranslationKey;
          }
        | {
              noTranslation: true;
              isCurrency?: false;
              currency?: string;
              text: string;
          }
        | {
              noTranslation?: false;
              isCurrency: true;
              currency: string;
              text: number;
          }
    );

export const Title: React.FunctionComponent<Props> = ({
    weight = 'medium',
    text,
    values,
    noTranslation = false,
    isCurrency,
    currency,
    ...otherProps
}) => {
    const { t, i18n } = useTranslation();
    return (
        <TitleStyle {...otherProps} weight={weight}>
            {noTranslation && text}
            {isCurrency && currency && computePrice(i18n, text, currency)}
            {!noTranslation && !isCurrency && t(text as TranslationKey, values)}
        </TitleStyle>
    );
};
